import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { db } from "../firebase/config";
import {
  collection,
  getDocs,
  addDoc,
  deleteDoc,
  doc,
} from "firebase/firestore";
import { COLORS } from "../constants/colors.js";
import { FONTS } from "../constants/fonts.js";
import CustomButton from "../components/common/CustomButton";
import CustomInput from "../components/common/CustomInput";
import CustomModal from "../components/common/CustomModal";
import { PlusIcon, TrashIcon, EnvelopeIcon } from "@heroicons/react/24/outline";

const styles = {
  safeArea: {
    flex: 1,
    backgroundColor: COLORS.white,
  },
  container: {
    flex: 1,
    backgroundColor: COLORS.white,
    padding: "16px 24px",
    paddingTop: 16,
    minHeight: "100vh",
    maxWidth: 1400,
    margin: "0 auto",
  },
  titleContainer: {
    display: "flex",
    justifyContent: "flex-start",
    alignItems: "center",
    marginBottom: 12,
    animation: "fadeIn 0.5s ease-in-out",
  },
  title: {
    fontSize: 32,
    color: COLORS.text,
    fontFamily: FONTS.bold,
    fontWeight: 700,
  },
  emailItem: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: 20,
    backgroundColor: `${COLORS.secondary}f0`,
    backdropFilter: "blur(10px)",
    WebkitBackdropFilter: "blur(10px)",
    borderRadius: 12,
    marginBottom: 12,
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
    border: `1px solid ${COLORS.tertiary}`,
    transition: "all 0.3s ease",
    animation: "fadeIn 0.5s ease-in-out",
    "&:hover": {
      transform: "translateY(-4px)",
      boxShadow: `0 6px 16px rgba(${parseInt(
        COLORS.primary.slice(1, 3),
        16
      )}, ${parseInt(COLORS.primary.slice(3, 5), 16)}, ${parseInt(
        COLORS.primary.slice(5, 7),
        16
      )}, 0.2)`,
    },
  },
  emailInfo: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    flex: 1,
  },
  emailIcon: {
    width: 28,
    height: 28,
    color: COLORS.primary,
  },
  emailText: {
    fontSize: 16,
    color: COLORS.text,
    fontFamily: FONTS.regularInter,
  },
  actionsContainer: {
    display: "flex",
    alignItems: "center",
    gap: 12,
  },
  actionButton: {
    padding: 10,
    borderRadius: 12,
    transition: "all 0.3s ease",
    cursor: "pointer",
    "&:hover": {
      backgroundColor: COLORS.error,
      "& svg": {
        stroke: COLORS.white,
      },
    },
  },
  addButton: {
    position: "fixed",
    bottom: 32,
    right: 32,
    border: "none",
    borderRadius: 32,
    width: 64,
    height: 64,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: COLORS.primary,
    boxShadow: "0 4px 16px rgba(0, 0, 0, 0.2)",
    cursor: "pointer",
    color: COLORS.white,
    transition: "all 0.3s ease",
    "&:hover": {
      backgroundColor: COLORS.secondary,
      color: COLORS.primary,
      transform: "scale(1.1)",
      boxShadow: `0 6px 20px rgba(${parseInt(
        COLORS.primary.slice(1, 3),
        16
      )}, ${parseInt(COLORS.primary.slice(3, 5), 16)}, ${parseInt(
        COLORS.primary.slice(5, 7),
        16
      )}, 0.3)`,
    },
    "&:active": {
      transform: "scale(0.95)",
    },
  },
  emptyText: {
    fontSize: 18,
    color: COLORS.gray,
    textAlign: "center",
    marginTop: 32,
    fontFamily: FONTS.regularInter,
  },
  errorText: {
    fontSize: 16,
    color: COLORS.error,
    textAlign: "center",
    marginBottom: 12,
    fontFamily: FONTS.regularInter,
  },
  loader: {
    fontSize: 18,
    color: COLORS.text,
    textAlign: "center",
    marginVertical: 16,
    fontFamily: FONTS.regularInter,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
  },
  loadingSpinner: {
    border: `3px solid ${COLORS.gray}`,
    borderTop: `3px solid ${COLORS.primary}`,
    borderRadius: "50%",
    width: 24,
    height: 24,
    animation: "spin 1s linear infinite",
  },
  "@keyframes spin": {
    "0%": { transform: "rotate(0deg)" },
    "100%": { transform: "rotate(360deg)" },
  },
  "@keyframes fadeIn": {
    "0%": { opacity: 0, transform: "translateY(10px)" },
    "100%": { opacity: 1, transform: "translateY(0)" },
  },
  "@media (max-width: 768px)": {
    container: {
      padding: "12px 16px",
      paddingTop: 16,
    },
    title: {
      fontSize: 28,
    },
    emailItem: {
      flexDirection: "column",
      alignItems: "flex-start",
      gap: 12,
      padding: 16,
    },
    actionsContainer: {
      width: "100%",
      justifyContent: "flex-end",
    },
    addButton: {
      bottom: 16,
      right: 16,
      width: 56,
      height: 56,
    },
  },
};

const AdminEmailManagementScreen = () => {
  const { user, userType } = useAuth();
  const navigate = useNavigate();
  const [emails, setEmails] = useState([]);
  const [newEmail, setNewEmail] = useState("");
  const [isModalVisible, setModalVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    if (!user || userType !== "admin") {
      navigate("/");
      return;
    }

    const fetchEmails = async () => {
      try {
        setLoading(true);
        const querySnapshot = await getDocs(collection(db, "emailRecipients"));
        const emailList = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          email: doc.data().email,
        }));
        setEmails(emailList);
      } catch (error) {
        console.error("Error fetching emails:", error);
        setError("Failed to fetch email recipients");
      } finally {
        setLoading(false);
      }
    };

    fetchEmails();
  }, [user, userType, navigate]);

  const handleAddEmail = async () => {
    if (!newEmail) {
      setError("Please enter an email address");
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newEmail)) {
      setError("Please enter a valid email address");
      return;
    }
    try {
      setLoading(true);
      setError("");
      const docRef = await addDoc(collection(db, "emailRecipients"), {
        email: newEmail,
      });
      setEmails([...emails, { id: docRef.id, email: newEmail }]);
      setNewEmail("");
      setModalVisible(false);
    } catch (error) {
      console.error("Error adding email:", error);
      setError("Failed to add email: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteEmail = async (id) => {
    if (!window.confirm("Are you sure you want to delete this email?")) return;
    setDeletingId(id);
    try {
      await deleteDoc(doc(db, "emailRecipients", id));
      setEmails(emails.filter((email) => email.id !== id));
    } catch (error) {
      console.error("Error deleting email:", error);
      setError("Failed to delete email: " + error.message);
    } finally {
      setDeletingId(null);
    }
  };

  const renderEmailItem = (item) => (
    <div style={styles.emailItem} key={item.id}>
      <div style={styles.emailInfo}>
        <EnvelopeIcon style={styles.emailIcon} />
        <span style={styles.emailText}>{item.email}</span>
      </div>
      <div style={styles.actionsContainer}>
        <button
          style={{ ...styles.actionButton }}
          onClick={() => handleDeleteEmail(item.id)}
          disabled={deletingId === item.id}
        >
          {deletingId === item.id ? (
            <div style={styles.loadingSpinner}></div>
          ) : (
            <TrashIcon width={24} height={24} stroke={COLORS.error} />
          )}
        </button>
      </div>
    </div>
  );

  return (
    <div style={styles.safeArea}>
      <div style={styles.container}>
        <div style={styles.titleContainer}>
          <h1 style={styles.title}>Email Recipients</h1>
        </div>
        {error && <div style={styles.errorText}>{error}</div>}
        {loading ? (
          <div style={styles.loader}>
            <div style={styles.loadingSpinner}></div>
            <span>Loading...</span>
          </div>
        ) : emails.length === 0 ? (
          <div style={styles.emptyText}>No email recipients found</div>
        ) : (
          <div>{emails.map(renderEmailItem)}</div>
        )}
        <button
          style={styles.addButton}
          onClick={() => {
            setNewEmail("");
            setError("");
            setModalVisible(true);
          }}
          disabled={loading}
        >
          <PlusIcon width={30} height={30} stroke={COLORS.white} />
        </button>
        <CustomModal
          isVisible={isModalVisible}
          onClose={() => setModalVisible(false)}
          title="Add Email Recipient"
          style={{
            backgroundColor: COLORS.white,
            border: `1px solid ${COLORS.tertiary}`,
            boxShadow: "0 8px 24px rgba(0, 0, 0, 0.15)",
            borderRadius: 12,
            padding: 16,
            maxWidth: 500,
            width: "90%",
            margin: "0 auto",
          }}
        >
          <CustomInput
            value={newEmail}
            onChangeText={setNewEmail}
            placeholder="Enter email address"
            icon="mail"
            style={{ marginBottom: 12 }}
          />
          <CustomButton
            title={loading ? "Saving..." : "Add Email"}
            onPress={handleAddEmail}
            disabled={loading}
            style={{ marginTop: 12, borderRadius: 12 }}
          />
        </CustomModal>
      </div>
    </div>
  );
};

export default AdminEmailManagementScreen;
